import { useState } from 'react'
import { useTranslation } from '../../i18n'
import { COLORS } from '../../lib/theme'
import { DocIcon } from '../DocIcon'
import type { PlanHuntDocItem } from './types'

export interface PlanHuntTeamDocsPickerProps {
  docs: PlanHuntDocItem[]
  alreadyPicked: string[]
  onCancel: () => void
  onConfirm: (ids: string[]) => void
}

export function PlanHuntTeamDocsPicker({ docs, alreadyPicked, onCancel, onConfirm }: PlanHuntTeamDocsPickerProps) {
  const { t } = useTranslation('planHunt')
  const [picked, setPicked] = useState<Set<string>>(() => new Set())
  const toggle = (id: string) => {
    const next = new Set(picked)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setPicked(next)
  }
  const available = docs.filter((d) => !alreadyPicked.includes(d.id))

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        background: 'rgba(10, 20, 15, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.surfaceContainerLowest,
          borderRadius: 12,
          width: '100%',
          maxWidth: 520,
          boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '18px 22px 6px' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.onSurface }}>{t('teamDocsPicker.title')}</div>
          <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 2 }}>
            {t('teamDocsPicker.subtitle')}
          </div>
        </div>
        <div style={{ padding: '10px 0', maxHeight: 360, overflowY: 'auto' }}>
          {available.map((d) => {
            const sel = picked.has(d.id)
            return (
              <label
                key={d.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 22px',
                  cursor: 'pointer',
                  background: sel ? COLORS.primaryOpacity8 : 'transparent',
                }}
              >
                <input type="checkbox" checked={sel} onChange={() => toggle(d.id)} aria-label={d.name} className="wh-checkbox" />
                <DocIcon kind={d.kind} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{d.name}</div>
                  {d.size && <div style={{ fontSize: 11, color: COLORS.outline }}>{d.size}</div>}
                </div>
              </label>
            )
          })}
          {available.length === 0 && (
            <div style={{ padding: 28, textAlign: 'center', fontSize: 13, color: COLORS.outline }}>
              {t('teamDocsPicker.allAttached')}
            </div>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
            padding: '12px 22px',
            borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
            background: COLORS.surface,
          }}
        >
          <button type="button" className="wh-btn wh-btn-outline" onClick={onCancel}>
            {t('teamDocsPicker.cancel')}
          </button>
          <button
            type="button"
            className="wh-btn wh-btn-primary"
            onClick={() => onConfirm([...picked])}
            disabled={picked.size === 0}
          >
            {picked.size > 0 ? t('teamDocsPicker.attachWithCount', { count: picked.size }) : t('teamDocsPicker.attach')}
          </button>
        </div>
      </div>
    </div>
  )
}
